/**
 * NineSliceButton - 9スライスボタンコンポーネント
 *
 * 小さな画像素材から任意サイズのボタンを生成する
 * 5つの状態（default, hover, on, on_hover, active）に対応
 *
 * Usage:
 * <NineSliceButton onClick={handleClick}>
 *   ボタンテキスト
 * </NineSliceButton>
 *
 * <NineSliceButton isOn={true} onClick={handleToggle}>
 *   トグルボタン
 * </NineSliceButton>
 */

import React, { useState, useEffect } from 'react';
import { colors, borderRadius, transitions } from '../../styles';

// ========================================
// 型定義
// ========================================

type ButtonState = 'default' | 'hover' | 'on' | 'on_hover' | 'active';

interface NineSliceButtonConfig {
  basePath: string;
  sliceSize: number;
  imageSize: number;
}

interface NineSliceButtonProps {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  isOn?: boolean;
  disabled?: boolean;
  width?: number | string;
  height?: number | string;
  minWidth?: number | string;
  padding?: number | string;
  style?: React.CSSProperties;
  className?: string;
}

// ========================================
// ボタン設定
// ========================================

const BUTTON_CONFIG: NineSliceButtonConfig = {
  basePath: '/images/ui/buttons/',
  sliceSize: 3,
  imageSize: 12,
};

const BUTTON_IMAGES: Record<ButtonState, string> = {
  default: 'button_default.png',
  hover: 'button_hover.png',
  on: 'button_on.png',
  on_hover: 'button_on_hover.png',
  active: 'button_active.png',
};

// フォールバックスタイル
const FALLBACK_STYLES: Record<ButtonState, React.CSSProperties> = {
  default: {
    background: `linear-gradient(180deg, ${colors.background.secondary} 0%, ${colors.background.primary} 100%)`,
    border: `1px solid ${colors.border.default}`,
  },
  hover: {
    background: `linear-gradient(180deg, #3a3a5a 0%, ${colors.background.secondary} 100%)`,
    border: `1px solid #5a5a7a`,
  },
  on: {
    background: `linear-gradient(180deg, ${colors.accent.purple} 0%, ${colors.accent.purpleDark} 100%)`,
    border: `1px solid ${colors.accent.purpleLight}`,
  },
  on_hover: {
    background: `linear-gradient(180deg, ${colors.accent.purpleLight} 0%, ${colors.accent.purple} 100%)`,
    border: `1px solid ${colors.accent.purpleLight}`,
  },
  active: {
    background: `linear-gradient(180deg, ${colors.background.primary} 0%, ${colors.background.tertiary} 100%)`,
    border: `1px solid ${colors.border.default}`,
  },
};

// ========================================
// コンポーネント
// ========================================

export const NineSliceButton: React.FC<NineSliceButtonProps> = ({
  children,
  onClick,
  isOn = false,
  disabled = false,
  width,
  height,
  minWidth = 80,
  padding = '8px 16px',
  style,
  className,
}) => {
  const [buttonState, setButtonState] = useState<ButtonState>(isOn ? 'on' : 'default');
  const [useNineSlice, setUseNineSlice] = useState(true);
  const [isPressed, setIsPressed] = useState(false);

  // 画像の存在チェック
  useEffect(() => {
    const img = new Image();
    img.onload = () => setUseNineSlice(true);
    img.onerror = () => setUseNineSlice(false);
    img.src = `${BUTTON_CONFIG.basePath}${BUTTON_IMAGES.default}`;
  }, []);

  // isOn プロップの変更を反映
  useEffect(() => {
    if (!isPressed) {
      setButtonState(isOn ? 'on' : 'default');
    }
  }, [isOn, isPressed]);

  const handleMouseEnter = () => {
    if (disabled) return;
    setButtonState(isOn ? 'on_hover' : 'hover');
  };

  const handleMouseLeave = () => {
    if (disabled) return;
    setIsPressed(false);
    setButtonState(isOn ? 'on' : 'default');
  };

  const handleMouseDown = () => {
    if (disabled) return;
    setIsPressed(true);
    setButtonState('active');
  };

  const handleMouseUp = () => {
    if (disabled) return;
    setIsPressed(false);
    setButtonState(isOn ? 'on_hover' : 'hover');
  };

  const handleClick = (e: React.MouseEvent) => {
    if (disabled || !onClick) return;
    onClick(e);
  };

  // 9スライスのborder-imageスタイル
  const getImagePath = () => `${BUTTON_CONFIG.basePath}${BUTTON_IMAGES[buttonState]}`;

  const nineSliceStyle: React.CSSProperties = {
    borderStyle: 'solid',
    borderWidth: BUTTON_CONFIG.sliceSize,
    borderImage: `url(${getImagePath()}) ${BUTTON_CONFIG.sliceSize} fill / ${BUTTON_CONFIG.sliceSize}px / 0 stretch`,
    background: 'transparent',
  };

  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    width,
    height,
    minWidth,
    padding,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    color: colors.text.primary,
    fontSize: '14px',
    fontWeight: 500,
    transition: transitions.fast,
    borderRadius: useNineSlice ? 0 : borderRadius.md,
    ...(useNineSlice ? nineSliceStyle : FALLBACK_STYLES[buttonState]),
    ...style,
  };

  return (
    <button
      style={baseStyle}
      className={className}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

export default NineSliceButton;
